import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, ConfigProvider, message } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { API_BASE_URL } from '../../config';
import { paginateOrders } from '../../utils/adminOrderTable.mjs';
import '../AdminDashboard.css';

const CUSTOMER_TEMPLATE_SETTING_KEY = 'customer_whatsapp_template';

const formatCustomerLastOrderDate = (lastOrderDate) => {
    if (!lastOrderDate) return '';
    const value = String(lastOrderDate);
    return value.includes('T') ? value.split('T')[0] : value;
};

const renderCustomerTemplateMessage = (template, customer) =>
    String(template || '')
        .replace(/\{customer_name\}/g, customer?.customer_name || '')
        .replace(/\{last_order\}/g, formatCustomerLastOrderDate(customer?.last_order_date));

const buildCustomerWhatsAppUrl = (contactNumber, message = '') => {
    const digits = String(contactNumber || '').replace(/\D+/g, '');
    const baseUrl = `https://wa.me/${digits}`;
    return message ? `${baseUrl}?text=${encodeURIComponent(message)}` : baseUrl;
};

function CustomersTab() {
    const navigate = useNavigate();
    const token = localStorage.getItem('adminToken');

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    }), [token]);

    const handleAuthError = useCallback((res) => {
        if (res.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUsername');
            navigate('/admin');
            return true;
        }
        return false;
    }, [navigate]);

    const [customers, setCustomers] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerPage, setCustomerPage] = useState(1);
    const [customerSearch, setCustomerSearch] = useState('');

    const [deleteCustomerTarget, setDeleteCustomerTarget] = useState(null);
    const [customerDeleting, setCustomerDeleting] = useState(false);

    const [customerTemplateMessage, setCustomerTemplateMessage] = useState('');
    const [customerTemplateDraft, setCustomerTemplateDraft] = useState('');
    const [showCustomerTemplateModal, setShowCustomerTemplateModal] = useState(false);
    const [customerTemplateSaving, setCustomerTemplateSaving] = useState(false);

    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [addCustomerForm, setAddCustomerForm] = useState({ customer_name: '', country_phone: '65' });
    const [addCustomerSaving, setAddCustomerSaving] = useState(false);
    const [addCustomerError, setAddCustomerError] = useState('');

    const [zohoSyncing, setZohoSyncing] = useState(false);
    const [zohoSyncResult, setZohoSyncResult] = useState(null);

    const [editContactTarget, setEditContactTarget] = useState(null);
    const [editContactPhone, setEditContactPhone] = useState('');
    const [editContactSaving, setEditContactSaving] = useState(false);
    const [editContactError, setEditContactError] = useState('');

    const fetchCustomers = useCallback(async () => {
        setCustomerLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/customers`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setCustomers(data.customers || []);
        } catch (err) {
            // Non-fatal — table just stays empty and the user can retry.
        } finally {
            setCustomerLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const fetchCustomerTemplateMessage = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/settings/${CUSTOMER_TEMPLATE_SETTING_KEY}`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            const value = data.value || '';
            setCustomerTemplateMessage(value);
            setCustomerTemplateDraft(value);
        } catch (err) {
            // Non-fatal — template just falls back to empty/default.
        }
    }, [authHeaders, handleAuthError]);

    useEffect(() => {
        fetchCustomers();
        fetchCustomerTemplateMessage();
    }, [fetchCustomers, fetchCustomerTemplateMessage]);

    const handleSyncZohoContacts = async () => {
        setZohoSyncing(true);
        setZohoSyncResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/zoho/sync-contacts`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Sync failed');
            setZohoSyncResult({ success: true, synced: data.synced, skipped: data.skipped, contacts: data.contacts || [] });
            setCustomerPage(1);
            fetchCustomers();
        } catch (err) {
            setZohoSyncResult({ success: false, error: err.message });
        } finally {
            setZohoSyncing(false);
        }
    };

    const handleAddCustomer = async () => {
        if (!addCustomerForm.customer_name.trim() || !addCustomerForm.country_phone.trim()) {
            setAddCustomerError('Customer name and contact number are required');
            return;
        }
        const contact_number = '+' + addCustomerForm.country_phone;
        setAddCustomerSaving(true);
        setAddCustomerError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/customers`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ customer_name: addCustomerForm.customer_name, contact_number }),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) {
                setAddCustomerError(data.error || 'Failed to add customer');
                return;
            }
            setShowAddCustomerModal(false);
            setAddCustomerForm({ customer_name: '', country_phone: '65' });
            fetchCustomers();
        } catch (err) {
            setAddCustomerError(err.message);
        } finally {
            setAddCustomerSaving(false);
        }
    };

    const handleDeleteCustomer = async () => {
        if (!deleteCustomerTarget) return;
        const name = deleteCustomerTarget.customer_name;
        setCustomerDeleting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/customers/${deleteCustomerTarget.id}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) {
                message.error(data.error || 'Failed to delete customer');
                return;
            }
            setDeleteCustomerTarget(null);
            fetchCustomers();
            message.success(
                data.zoho_deleted
                    ? `${name} deleted from local records and Zoho.`
                    : `${name} deleted successfully.`
            );
        } catch (err) {
            message.error(err.message);
        } finally {
            setCustomerDeleting(false);
        }
    };

    const handleEditCustomerTemplateMessage = () => {
        setCustomerTemplateDraft(customerTemplateMessage);
        setShowCustomerTemplateModal(true);
    };

    const handleSaveCustomerTemplateMessage = async () => {
        setCustomerTemplateSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/settings/${CUSTOMER_TEMPLATE_SETTING_KEY}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ value: customerTemplateDraft })
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save template message');
            setCustomerTemplateMessage(data.value || '');
            setShowCustomerTemplateModal(false);
        } catch (err) {
            message.error(err.message);
        } finally {
            setCustomerTemplateSaving(false);
        }
    };

    const openEditContactModal = (customer) => {
        setEditContactTarget(customer);
        setEditContactPhone(String(customer.contact_number || '').replace(/^\+/, ''));
        setEditContactError('');
    };

    const closeEditContactModal = () => {
        setEditContactTarget(null);
        setEditContactPhone('');
        setEditContactError('');
    };

    const handleUpdateContact = async () => {
        if (!editContactPhone.trim() || editContactPhone.trim().length < 4) {
            setEditContactError('Enter a valid contact number');
            return;
        }
        setEditContactSaving(true);
        setEditContactError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/customers/${editContactTarget.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ contact_number: '+' + editContactPhone }),
            });
            if (handleAuthError(res)) return;
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update contact number');
            closeEditContactModal();
            fetchCustomers();
            message.success('Contact number updated — string orders for this customer will reflect it immediately.');
        } catch (err) {
            setEditContactError(err.message);
        } finally {
            setEditContactSaving(false);
        }
    };

    const filteredCustomers = customerSearch.trim()
        ? customers.filter(c => {
            const q = customerSearch.trim().toLowerCase();
            const qDigits = q.replace(/\D/g, '');
            return c.customer_name?.toLowerCase().includes(q) ||
                c.contact_number?.toLowerCase().includes(q) ||
                (qDigits.length > 0 && c.contact_number?.replace(/\D/g, '').includes(qDigits));
        })
        : customers;
    const paginatedCustomers = paginateOrders(filteredCustomers, customerPage, 25);

    return (
        <div className="tab-content">
            <div className="section-header">
                <h2>Customer Info</h2>
                <div className="section-header-actions">
                    <button className="btn-db" onClick={handleEditCustomerTemplateMessage}>Template Message</button>
                    <button
                        className="btn-db"
                        onClick={handleSyncZohoContacts}
                        disabled={zohoSyncing}
                        style={{ background: zohoSyncing ? '#ccc' : '#4a90d9', color: '#fff', borderColor: 'transparent' }}
                    >
                        {zohoSyncing ? 'Syncing...' : 'Sync Zoho'}
                    </button>
                    <button className="btn-add" onClick={() => { setAddCustomerForm({ customer_name: '', country_phone: '65' }); setAddCustomerError(''); setShowAddCustomerModal(true); }}>+ Add Customer</button>
                </div>
            </div>

            <div style={{ margin: '12px 0' }}>
                <Input
                    size="large"
                    placeholder="Search by name or contact number..."
                    value={customerSearch}
                    onChange={e => { setCustomerSearch(e.target.value); setCustomerPage(1); }}
                    allowClear
                    style={{ maxWidth: 360 }}
                />
            </div>

            {customerLoading ? (
                <div className="admin-loading">Loading...</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th>Contact</th>
                                <th>Email</th>
                                <th>Racquets</th>
                                <th>Orders</th>
                                <th>Last Order</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCustomers.items.map(customer => (
                                <tr key={customer.id}>
                                    <td>{customer.customer_name}</td>
                                    <td>{customer.contact_number}</td>
                                    <td>{customer.email || '—'}</td>
                                    <td>{customer.racquet_count || 0}</td>
                                    <td>{customer.order_count || 0}</td>
                                    <td>{customer.last_order_date ? new Date(customer.last_order_date).toLocaleDateString() : '—'}</td>
                                    <td className="action-cell">
                                        {(() => {
                                            const templateMsg = renderCustomerTemplateMessage(customerTemplateMessage, customer);
                                            const whatsappUrl = buildCustomerWhatsAppUrl(customer.contact_number, templateMsg);
                                            return (
                                                <>
                                                    <a
                                                        href={whatsappUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-contact"
                                                        aria-label={`Contact ${customer.customer_name} on WhatsApp`}
                                                        title={templateMsg ? `Open WhatsApp with template message for ${customer.customer_name}` : 'Open WhatsApp'}
                                                    >
                                                        <MessageOutlined />
                                                    </a>
                                                    <button
                                                        style={{ marginLeft: 10 }}
                                                        className="btn-cancel"
                                                        title={`Edit contact number for ${customer.customer_name}`}
                                                        onClick={() => openEditContactModal(customer)}
                                                    >
                                                        Edit Contact
                                                    </button>
                                                    <button
                                                        style={{ marginLeft: 10 }}
                                                        className="btn-delete"
                                                        title={`Delete ${customer.customer_name}`}
                                                        onClick={() => setDeleteCustomerTarget(customer)}
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredCustomers.length === 0 && (
                        <div className="no-data">{customerSearch.trim() ? 'No customers match your search.' : 'No customer records found yet.'}</div>
                    )}
                    {filteredCustomers.length > 0 && (
                        <div className="table-pagination">
                            <span className="table-pagination-summary">
                                Showing {paginatedCustomers.items.length} of {paginatedCustomers.totalItems} customer{paginatedCustomers.totalItems !== 1 ? 's' : ''}{customerSearch.trim() ? ` (filtered from ${customers.length})` : ''}
                            </span>
                            <div className="table-pagination-controls">
                                <button
                                    type="button"
                                    className="btn-cancel table-page-btn"
                                    onClick={() => setCustomerPage(prev => Math.max(1, prev - 1))}
                                    disabled={paginatedCustomers.currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className="table-page-indicator">
                                    Page {paginatedCustomers.currentPage} of {paginatedCustomers.totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="btn-save table-page-btn"
                                    onClick={() => setCustomerPage(prev => Math.min(paginatedCustomers.totalPages, prev + 1))}
                                    disabled={paginatedCustomers.currentPage === paginatedCustomers.totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {zohoSyncResult && (
                <div style={{ marginTop: 16 }}>
                    <div className={`zoho-test-result ${zohoSyncResult.success ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
                        {zohoSyncResult.success
                            ? `Sync complete — ${zohoSyncResult.synced} contact(s) saved, ${zohoSyncResult.skipped} skipped (no phone/name)`
                            : `Sync error: ${zohoSyncResult.error}`}
                    </div>
                    {zohoSyncResult.success && zohoSyncResult.contacts?.length > 0 && (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Zoho Contact ID</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zohoSyncResult.contacts.map((c, i) => (
                                        <tr key={c.zoho_contact_id || i}>
                                            <td><code style={{ fontSize: '0.78rem' }}>{c.zoho_contact_id || '—'}</code></td>
                                            <td>{c.name}</td>
                                            <td>{c.phone}</td>
                                            <td>{c.email || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {showCustomerTemplateModal && (
                <div className="modal-overlay" onClick={() => setShowCustomerTemplateModal(false)}>
                    <div className="modal-content template-message-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowCustomerTemplateModal(false)}>×</button>
                        <h2>Template Message</h2>
                        <form
                            className="admin-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSaveCustomerTemplateMessage();
                            }}
                        >
                            <div className="form-group">
                                <label>WhatsApp Template</label>
                                <textarea
                                    rows="8"
                                    value={customerTemplateDraft}
                                    onChange={(e) => setCustomerTemplateDraft(e.target.value)}
                                    placeholder="Hi {customer_name}, your racquet is ready for collection."
                                />
                                <small className="form-help-text">Use <code>{'{customer_name}'}</code> to insert the customer name.</small>
                            </div>
                            <div className="template-preview-card">
                                <div className="template-preview-label">Preview</div>
                                <div className="template-preview-body">
                                    {renderCustomerTemplateMessage(customerTemplateDraft, { customer_name: 'Alex Tan' }) || 'Message preview will appear here.'}
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-cancel" onClick={() => setShowCustomerTemplateModal(false)}>Cancel</button>
                                <button type="submit" className="btn-save" disabled={customerTemplateSaving}>
                                    {customerTemplateSaving ? 'Saving...' : 'Save Template'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showAddCustomerModal && (
                <div className="modal-overlay" onClick={() => { setShowAddCustomerModal(false); setAddCustomerError(''); setAddCustomerForm({ customer_name: '', country_phone: '65' }); }}>
                    <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => { setShowAddCustomerModal(false); setAddCustomerError(''); setAddCustomerForm({ customer_name: '', country_phone: '65' }); }}>×</button>
                        <h3 style={{ marginBottom: 16 }}>Add Customer</h3>
                        <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                            <Form layout="vertical" requiredMark="">
                                <Form.Item label="Customer Name" required>
                                    <Input
                                        size="large"
                                        value={addCustomerForm.customer_name}
                                        onChange={e => { setAddCustomerForm(p => ({ ...p, customer_name: e.target.value })); setAddCustomerError(''); }}
                                        placeholder="Full name"
                                    />
                                </Form.Item>
                                <Form.Item label="Contact Number" required>
                                    <PhoneInput
                                        country="sg"
                                        preferredCountries={['sg', 'my', 'id', 'ph', 'th', 'au', 'gb', 'us']}
                                        enableSearch
                                        searchPlaceholder="Search country..."
                                        value={addCustomerForm.country_phone}
                                        onChange={val => { setAddCustomerForm(p => ({ ...p, country_phone: val })); setAddCustomerError(''); }}
                                        containerClass="order-phone-container"
                                        inputClass="order-phone-input"
                                        buttonClass="order-phone-flag"
                                    />
                                </Form.Item>
                                {addCustomerError && (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        marginBottom: 12,
                                        fontSize: '0.85rem',
                                        color: '#cf1322',
                                    }}>
                                        {addCustomerError}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={() => { setShowAddCustomerModal(false); setAddCustomerError(''); setAddCustomerForm({ customer_name: '', country_phone: '65' }); }}>Cancel</button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={addCustomerSaving}
                                        disabled={!addCustomerForm.customer_name.trim() || addCustomerForm.country_phone.length < 4}
                                        onClick={handleAddCustomer}
                                    >
                                        Save Customer
                                    </Button>
                                </div>
                            </Form>
                        </ConfigProvider>
                    </div>
                </div>
            )}

            {editContactTarget && (
                <div className="modal-overlay" onClick={closeEditContactModal}>
                    <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeEditContactModal}>×</button>
                        <h3 style={{ marginBottom: 16 }}>Edit Contact — {editContactTarget.customer_name}</h3>
                        <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                            <Form layout="vertical" requiredMark="">
                                <Form.Item label="Contact Number" required>
                                    <PhoneInput
                                        country="sg"
                                        preferredCountries={['sg', 'my', 'id', 'ph', 'th', 'au', 'gb', 'us']}
                                        enableSearch
                                        searchPlaceholder="Search country..."
                                        value={editContactPhone}
                                        onChange={val => { setEditContactPhone(val); setEditContactError(''); }}
                                        containerClass="order-phone-container"
                                        inputClass="order-phone-input"
                                        buttonClass="order-phone-flag"
                                    />
                                </Form.Item>
                                {editContactError && (
                                    <div style={{
                                        background: '#fff2f0',
                                        border: '1px solid #ffccc7',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        marginBottom: 12,
                                        fontSize: '0.85rem',
                                        color: '#cf1322',
                                    }}>
                                        {editContactError}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={closeEditContactModal}>Cancel</button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={editContactSaving}
                                        disabled={editContactPhone.length < 4}
                                        onClick={handleUpdateContact}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </ConfigProvider>
                    </div>
                </div>
            )}

            {deleteCustomerTarget && (
                <div className="modal-overlay" onClick={() => setDeleteCustomerTarget(null)}>
                    <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setDeleteCustomerTarget(null)}>×</button>
                        <h3 style={{ marginBottom: 12 }}>Delete Customer</h3>
                        <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: 20 }}>
                            Are you sure you want to delete <strong>{deleteCustomerTarget.customer_name}</strong> ({deleteCustomerTarget.contact_number})?
                            This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Button size="large" style={{ flex: 1 }} onClick={() => setDeleteCustomerTarget(null)}>
                                Cancel
                            </Button>
                            <Button
                                danger
                                type="primary"
                                size="large"
                                style={{ flex: 1 }}
                                loading={customerDeleting}
                                onClick={handleDeleteCustomer}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CustomersTab;
